import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { SOLUTION_PAGES, getSolutionPage } from "../data/solutionPages";
import { USE_CASES } from "../data/useCases";
import { ArrowRight, ArrowLeft, Gift, Quote, ChevronRight, ChevronDown, Play, BookOpen } from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        data-testid={testid}
      >
        <span className="font-heading text-base font-medium text-slate-900">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}

function BrowserFrame({ src, alt }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)] overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </div>
      <img src={src} alt={alt} className="w-full block" loading="lazy" />
    </div>
  );
}

export default function SolutionPage() {
  const { slug } = useParams();
  const solution = getSolutionPage(slug);

  useEffect(() => {
    if (solution) document.title = solution.seoTitle;
    window.scrollTo(0, 0);
  }, [solution]);

  if (!solution) return <Navigate to="/solutions" replace />;

  const idx = SOLUTION_PAGES.findIndex((s) => s.slug === slug);
  const prev = SOLUTION_PAGES[(idx - 1 + SOLUTION_PAGES.length) % SOLUTION_PAGES.length];
  const next = SOLUTION_PAGES[(idx + 1) % SOLUTION_PAGES.length];
  const useCases = (solution.relatedCaseStudies || [])
    .map((s) => USE_CASES.find((c) => c.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 pt-8 md:pt-10">
        <nav className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="solution-breadcrumb">
          <Link to="/" className="hover:text-slate-700">Accueil</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/solutions" className="hover:text-slate-700">Solutions</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-700 font-medium">{solution.menuLabel}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 md:pt-10 pb-16" data-testid="solution-hero">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className={`${OVERLINE} mb-4`}>{solution.overline}</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-slate-900" data-testid="solution-title">
              {solution.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">{solution.subtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register?context=vendor"
                className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                data-testid="solution-cta-start"
              >
                <Gift className="h-4 w-4" /> Commencer gratuitement
              </Link>
              <button
                type="button"
                onClick={openInteractiveDemo}
                className="btn-jn-secondary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                data-testid="solution-cta-demo"
              >
                <Play className="h-4 w-4" /> Voir la démo interactive
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-500">30 jours d'essai gratuit · Sans carte bancaire · Résiliable en 1 clic</p>
          </div>
          <div className="min-w-0" data-testid="solution-mockup">
            <solution.Mockup />
          </div>
        </div>
      </section>

      {/* Le problème */}
      <section className="bg-slate-950 text-white" data-testid="solution-problem">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-400 mb-3">Le problème</div>
          <h2 className="font-heading text-2xl md:text-4xl font-medium tracking-tight leading-tight max-w-3xl">
            {solution.problem.title}
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl">{solution.problem.intro}</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {solution.problem.pains.map((p, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 flex items-start gap-3.5" data-testid={`solution-pain-${i}`}>
                <span className="h-9 w-9 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                  <p.icon className="h-4 w-4 text-red-400" />
                </span>
                <p className="text-sm text-slate-300 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* La réponse Junago */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="solution-response">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className={`${OVERLINE} mb-3`}>La réponse Junago</div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900 leading-snug">
              {solution.response.title}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {solution.response.paragraphs.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-slate-600 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Fonctionnalités utilisées */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="solution-modules">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className={`${OVERLINE} mb-3`}>Les fonctionnalités qui travaillent ensemble</div>
          <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Les modules Junago derrière cette solution</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {solution.modules.map((m) => (
              <Link
                key={m.label}
                to={m.to}
                className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-[0_12px_32px_-12px_rgba(124,58,237,0.25)] transition-all"
                data-testid={`solution-module-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <m.icon className="h-4.5 w-4.5 text-violet-600" style={{ height: 18, width: 18 }} />
                </div>
                <h3 className="mt-3.5 font-heading text-base font-medium text-slate-900 group-hover:text-violet-700 transition-colors">{m.label}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
                  Découvrir la fonctionnalité <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Parcours utilisateur */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="solution-workflow">
        <div className={`${OVERLINE} mb-3`}>Le parcours, étape par étape</div>
        <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">{solution.workflow.title}</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
          {solution.workflow.steps.map((s, i) => (
            <div key={s.label} className="relative flex gap-4" data-testid={`solution-step-${i}`}>
              <div className="flex flex-col items-center">
                <span className="h-9 w-9 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                {i < solution.workflow.steps.length - 1 && <span className="w-px flex-1 bg-slate-200 mt-2 hidden lg:block" style={{ minHeight: 12 }} />}
              </div>
              <div className="pt-1">
                <div className="font-heading text-base font-medium text-slate-900">{s.label}</div>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Captures du logiciel */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="solution-captures">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className={`${OVERLINE} mb-3`}>Le logiciel, en vrai</div>
          <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">À l'écran, ça ressemble à ça</h2>
          <div className="mt-10 space-y-12">
            {solution.captures.map((c, i) => (
              <figure key={i} data-testid={`solution-capture-${i}`}>
                {c.type === "img" ? (
                  <BrowserFrame src={c.src} alt={c.caption} />
                ) : (
                  <div className="max-w-3xl mx-auto"><solution.Mockup /></div>
                )}
                <figcaption className="mt-4 text-sm text-slate-500 text-center">{c.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignage */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="solution-testimonial">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-12 md:py-16">
          <div aria-hidden className="absolute -left-20 -top-32 h-80 w-80 rounded-full bg-violet-600 opacity-20 blur-3xl" />
          <div className="relative grid md:grid-cols-[auto,1fr] gap-8 md:gap-12 items-center">
            <img
              src={solution.quote.img}
              alt={solution.quote.author}
              className="h-28 w-28 md:h-36 md:w-36 rounded-2xl object-cover border border-white/15"
              loading="lazy"
            />
            <div>
              <Quote className="h-7 w-7 text-violet-400" />
              <blockquote className="mt-4 font-heading text-xl md:text-2xl font-medium tracking-tight leading-snug">
                «&nbsp;{solution.quote.text}&nbsp;»
              </blockquote>
              <div className="mt-4 text-sm">
                <span className="font-semibold">{solution.quote.author}</span>
                <span className="text-slate-400"> — {solution.quote.role}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Résultats */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="solution-results">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className={`${OVERLINE} mb-3`}>Ce que ça change</div>
          <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Des résultats concrets au quotidien</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {solution.results.map((r) => (
              <div key={r.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`solution-result-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <r.icon className="h-4.5 w-4.5 text-emerald-600" style={{ height: 18, width: 18 }} />
                </div>
                <h3 className="mt-3.5 font-heading text-base font-medium text-slate-900">{r.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cas d'usage associés */}
      {useCases.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-testid="solution-use-cases">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className={`${OVERLINE} mb-3`}>Junago en situation</div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Ces scénarios le montrent en pratique</h2>
            </div>
            <Link to="/cas-d-usage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700" data-testid="solution-all-use-cases">
              <BookOpen className="h-4 w-4" /> Tous les cas d'usage
            </Link>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {useCases.map((c) => (
              <Link
                key={c.slug}
                to={`/cas-d-usage/${c.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-[0_16px_40px_-16px_rgba(124,58,237,0.3)] transition-all"
                data-testid={`solution-use-case-${c.slug}`}
              >
                <div className="h-44 overflow-hidden">
                  <img src={c.heroImg} alt={c.heroAlt} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-violet-600">Cas d'usage</div>
                  <h3 className="mt-2 font-heading text-base font-medium text-slate-900 leading-snug group-hover:text-violet-700 transition-colors">{c.title}</h3>
                  <div className="mt-3 flex items-center gap-2.5">
                    <span className="h-7 w-7 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
                      <c.profile.icon className="h-3.5 w-3.5 text-violet-600" />
                    </span>
                    <span className="text-xs text-slate-500">{c.profile.label}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="solution-faq">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className={`${OVERLINE} mb-3 text-center`}>Questions fréquentes</div>
          <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900 text-center">Tout ce qu'on nous demande</h2>
          <div className="mt-8 space-y-3">
            {solution.faq.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} testid={`solution-faq-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Prev / Next */}
      <div className="max-w-6xl mx-auto px-6 pt-10 flex items-center justify-between text-sm">
        <Link to={`/solutions/${prev.slug}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium" data-testid="solution-prev">
          <ArrowLeft className="h-4 w-4" /> {prev.menuLabel}
        </Link>
        <Link to={`/solutions/${next.slug}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium" data-testid="solution-next">
          {next.menuLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20" data-testid="solution-cta-bottom">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
          <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Prêt à {solution.menuLabel.toLowerCase()} avec Junago ?
            </h2>
            <p className="mt-3 text-slate-400">30 jours d'essai complet, sans carte bancaire.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register?context=vendor"
                className="btn-jn-light inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold"
                data-testid="solution-bottom-start"
              >
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={openInteractiveDemo}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors"
                data-testid="solution-bottom-demo"
              >
                <Play className="h-4 w-4" /> Démo interactive
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
